/**
 * Der Zwei-Klick-Widerruf, bewusst ohne DOM.
 *
 * Das Frontend-Vitest laeuft mit environment: 'node', testbare Logik gehoert
 * deshalb hierher und nicht in die View. Gleiches Muster wie uninstall-flow.ts.
 *
 * Die Datei hiess bis 2026-08-09 `mcp-token-flow.ts` und trug zusaetzlich die
 * Aufbereitung der alten MCP-Token-Karte. Mit dem Ausbau des Token-Wegs blieb
 * nur noch dieser Mechanismus uebrig — er gehoert jetzt der OAuth-Karte, und
 * der alte Name haette den naechsten Aufraeumer dazu verleitet, die Datei
 * mitzuloeschen und der OAuth-Karte damit den Widerruf zu nehmen.
 */

export interface RevokeState {
  /**
   * Eintrag, der beim naechsten Druck wirklich widerrufen wird. Null = keiner scharf.
   *
   * Vertrag: `pressRevoke` entschaerft nur, wenn ein Klick auf einen ANDEREN
   * Eintrag passiert. Es gibt keinen Mechanismus, der `armedId` zuruecksetzt,
   * wenn sich die Liste OHNE Klick aendert (Hintergrund-Neuladen, ein zweiter
   * Tab, ein Widerruf von woanders). Der Aufrufer MUSS deshalb bei jedem
   * Neuladen der Liste `syncRevokeState` (oder mindestens `resetRevoke`)
   * aufrufen. Sonst faellt die Beschriftung nach einem Reload scheinbar auf
   * "Widerrufen" zurueck, obwohl intern noch scharf ist: Der Nutzer haelt den
   * naechsten Klick faelschlich fuer den ersten, tatsaechlich ist es der
   * zweite, und ein Zugang wird sofort und endgueltig widerrufen.
   */
  armedId: string | null;
}

export type RevokeAction = 'none' | 'revoke';

export interface RevokePressResult {
  state: RevokeState;
  action: RevokeAction;
  tokenId?: string;
}

export function createRevokeState(): RevokeState {
  return { armedId: null };
}

/**
 * Ein Druck braucht immer zwei Klicks, weil Widerruf endgueltig ist: einmal
 * Vertippen auf der falschen Zeile darf keinen echten Zugang sofort
 * unbrauchbar machen.
 *
 * Erster Druck schaerft nur scharf, loest aber nichts aus. Der zweite Druck auf
 * DENSELBEN Eintrag loest den Widerruf aus. Ein Druck auf einen ANDEREN
 * Eintrag, waehrend schon einer scharf ist, entschaerft den ersten wieder und
 * schaerft stattdessen den neuen: er loest NICHT den alten, scharfen aus. Sonst
 * wuerde ein Fehlklick (Maus wandert waehrend des zweiten Klicks auf eine
 * andere Zeile) einen voellig anderen, vielleicht noch aktiv genutzten Zugang
 * widerrufen, statt einfach nur den Vorgang neu zu starten.
 */
export function pressRevoke(s: RevokeState, id: string): RevokePressResult {
  if (s.armedId === id) {
    return { state: s, action: 'revoke', tokenId: id };
  }
  return { state: { armedId: id }, action: 'none' };
}

/**
 * Entschaerft bedingungslos, unabhaengig vom bisherigen Zustand.
 *
 * Der Aufrufer MUSS dies (oder `syncRevokeState`) bei jedem Neuladen der
 * Liste aufrufen, siehe Vertrag am `armedId`-Feld von `RevokeState`.
 */
export function resetRevoke(_s: RevokeState): RevokeState {
  return { armedId: null };
}

/**
 * Setzt die Schaerfung zurueck, wenn sich die Datengrundlage geaendert hat,
 * ohne dass ein Klick stattfand: der scharfe Eintrag ist aus der Liste
 * verschwunden, oder er wurde zwischenzeitlich (von woanders) widerrufen.
 *
 * Das ist die erzwingbare Variante des Vertrags an `RevokeState.armedId`:
 * Statt sich darauf zu verlassen, dass jeder Aufrufer beim Neuladen brav
 * `resetRevoke` ruft, prueft diese Funktion den konkreten Fall und entschaerft
 * nur dann, wenn es fachlich noetig ist. Ist der scharfe Eintrag weiterhin
 * unveraendert vorhanden, bleibt der Zustand unangetastet, ein laufender
 * Zwei-Klick-Vorgang wird also nicht durch jedes Neuladen unterbrochen.
 */
export function syncRevokeState(
  state: RevokeState,
  rows: ReadonlyArray<{ id: string; revoked_at: string | null }>,
): RevokeState {
  if (state.armedId === null) return state;
  const armedRow = rows.find((row) => row.id === state.armedId);
  const nochGueltig = armedRow !== undefined && armedRow.revoked_at === null;
  return nochGueltig ? state : { armedId: null };
}

export function revokeLabel(s: RevokeState, id: string): string {
  return s.armedId === id ? 'Wirklich widerrufen?' : 'Widerrufen';
}
